/**
 * Tenant Helper Utilities
 *
 * Team and admin use SEPARATE localStorage keys so that opening the admin panel
 * (and setting admin tenant) does not overwrite the team's tenant. When the user
 * returns to the team dashboard, team API requests still send the correct X-Tenant-ID.
 */

import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from './safeStorage';

// Team tenant keys (team dashboard / submit score / divisions / etc.)
const TEAM_TENANT_ID_KEY = 'teamTenantId';
const TEAM_TENANT_SLUG_KEY = 'teamTenantSlug';
const TEAM_TENANT_NAME_KEY = 'teamTenantName';

// Admin tenant keys (admin panel - which tenant the admin is viewing)
const ADMIN_TENANT_ID_KEY = 'adminTenantId';
const ADMIN_TENANT_SLUG_KEY = 'adminTenantSlug';
const ADMIN_TENANT_NAME_KEY = 'adminTenantName';
const ADMIN_TENANT_TYPE_KEY = 'adminTenantType';

// Legacy keys (migration: treat as team tenant if team keys not set)
const LEGACY_TENANT_ID_KEY = 'tenantId';
const LEGACY_TENANT_SLUG_KEY = 'tenantSlug';
const LEGACY_TENANT_NAME_KEY = 'tenantName';

// ---------- Team tenant (team dashboard, api.js) ----------

export const getTeamTenantId = () => {
  return getLocalStorageItem(TEAM_TENANT_ID_KEY) || getLocalStorageItem(LEGACY_TENANT_ID_KEY);
};

export const getTeamTenantSlug = () => {
  return getLocalStorageItem(TEAM_TENANT_SLUG_KEY) || getLocalStorageItem(LEGACY_TENANT_SLUG_KEY);
};

export const getTeamTenantName = () => {
  return getLocalStorageItem(TEAM_TENANT_NAME_KEY) || getLocalStorageItem(LEGACY_TENANT_NAME_KEY);
};

export const getTeamTenantInfo = () => {
  const id = getTeamTenantId();
  if (!id) return null;
  return {
    id,
    slug: getTeamTenantSlug(),
    name: getTeamTenantName(),
  };
};

export const setTeamTenantInfo = (tenant) => {
  if (tenant) {
    if (tenant.id) setLocalStorageItem(TEAM_TENANT_ID_KEY, tenant.id);
    if (tenant.slug) setLocalStorageItem(TEAM_TENANT_SLUG_KEY, tenant.slug);
    if (tenant.name) setLocalStorageItem(TEAM_TENANT_NAME_KEY, tenant.name);
  }
};

export const clearTeamTenantInfo = () => {
  removeLocalStorageItem(TEAM_TENANT_ID_KEY);
  removeLocalStorageItem(TEAM_TENANT_SLUG_KEY);
  removeLocalStorageItem(TEAM_TENANT_NAME_KEY);
  removeLocalStorageItem(LEGACY_TENANT_ID_KEY);
  removeLocalStorageItem(LEGACY_TENANT_SLUG_KEY);
  removeLocalStorageItem(LEGACY_TENANT_NAME_KEY);
};

// ---------- Admin tenant (admin panel, adminApi.js) ----------

export const getAdminTenantId = () => {
  return getLocalStorageItem(ADMIN_TENANT_ID_KEY);
};

export const getAdminTenantSlug = () => {
  return getLocalStorageItem(ADMIN_TENANT_SLUG_KEY);
};

export const getAdminTenantName = () => {
  return getLocalStorageItem(ADMIN_TENANT_NAME_KEY);
};

export const getAdminTenantType = () => {
  return getLocalStorageItem(ADMIN_TENANT_TYPE_KEY);
};

export const getAdminTenantInfo = () => {
  const id = getAdminTenantId();
  if (!id) return null;
  return {
    id,
    slug: getAdminTenantSlug(),
    name: getAdminTenantName(),
    type: getAdminTenantType(),
  };
};

export const setAdminTenantInfo = (tenant) => {
  if (tenant) {
    if (tenant.id) setLocalStorageItem(ADMIN_TENANT_ID_KEY, tenant.id);
    if (tenant.slug) setLocalStorageItem(ADMIN_TENANT_SLUG_KEY, tenant.slug);
    if (tenant.name) setLocalStorageItem(ADMIN_TENANT_NAME_KEY, tenant.name);
    if (tenant.type) setLocalStorageItem(ADMIN_TENANT_TYPE_KEY, tenant.type);
  }
};

export const clearAdminTenantInfo = () => {
  removeLocalStorageItem(ADMIN_TENANT_ID_KEY);
  removeLocalStorageItem(ADMIN_TENANT_SLUG_KEY);
  removeLocalStorageItem(ADMIN_TENANT_NAME_KEY);
  removeLocalStorageItem(ADMIN_TENANT_TYPE_KEY);
};

// ---------- Backward compatibility (default = team) ----------

/** Team tenant ID – used by api.js for team dashboard requests */
export const getTenantId = () => getTeamTenantId();

export const getTenantSlug = () => getTeamTenantSlug();
export const getTenantName = () => getTeamTenantName();

/** Team tenant info – used by AuthContext */
export const getTenantInfo = () => getTeamTenantInfo();

/**
 * Set tenant – call setTeamTenantInfo or setAdminTenantInfo explicitly from AuthContext vs Admin.
 * This setter writes to TEAM keys only so that legacy callers (team) don't break.
 */
export const setTenantInfo = (tenant) => setTeamTenantInfo(tenant);

/** Clear team tenant (team logout). Admin logout should call clearAdminTenantInfo. */
export const clearTenantInfo = () => clearTeamTenantInfo();

export const hasTenant = () => !!getTeamTenantId();

export default {
  getTenantId,
  getTenantSlug,
  getTenantName,
  getTenantInfo,
  setTenantInfo,
  clearTenantInfo,
  hasTenant,
  getTeamTenantId,
  getTeamTenantInfo,
  setTeamTenantInfo,
  clearTeamTenantInfo,
  getAdminTenantId,
  getAdminTenantInfo,
  setAdminTenantInfo,
  clearAdminTenantInfo,
};
